import { IRS_RAIL_BUTTON_LIST } from 'lib/constants';

export const formatWorkspaceTimestamp = (value) => {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMin = Math.floor(diffMs / 60000);
    const diffHr = Math.floor(diffMin / 60);
    const diffDay = Math.floor(diffHr / 24);

    if (diffMin < 1) return 'Just now';
    if (diffMin < 60) return `${diffMin} min ago`;
    if (diffHr < 24) return `${diffHr} hr ago`;

    const isYesterday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1).toDateString() === date.toDateString();
    if (isYesterday) return 'Yesterday';
    if (diffDay < 7) return `${diffDay} days ago`;

    if (date.getFullYear() === now.getFullYear()) {
        return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    }
    return date.toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
    });
};

const TENOR_TOKEN_URL = /^\d+(?:\.\d+)?[dwmy]$/i;
const TENOR_PAIR_FWD = /^(\d+(?:\.\d+)?[dwmy])\s+fwd\s+(\d+(?:\.\d+)?[dwmy])$/i;
const IMM_LABEL_RE = /^[zmuh]\d{1,2}$/i;
const DEFAULT_BASIS_REFERENCE_IRS_ID = '0194445a-3318-7444-8efd-b8f5bcd47e48';
const WORKSPACE_SHEET_COMMANDS = new Set(['HIST', 'FAVS']);
const COMMANDS_REQUIRING_STRUCTURE = new Set(
    (IRS_RAIL_BUTTON_LIST || [])
        .flatMap((section) => section?.items || [])
        .map((item) => String(item?.command || '').toUpperCase())
        .filter((command) => command && !WORKSPACE_SHEET_COMMANDS.has(command))
);

const parseForwardPairs = (structure) => {
    const raw = String(structure || '').trim();
    if (!raw) return [];
    const tokens = raw.toUpperCase().split(/\s+/).filter(Boolean);
    const pairs = [];
    for (let i = 0; i + 2 < tokens.length; i += 3) {
        if (tokens[i + 1] !== 'FWD') break;
        pairs.push([tokens[i], tokens[i + 2]]);
    }
    return pairs;
};

const getForwardPlotType = (pairs) => {
    if (!Array.isArray(pairs) || !pairs.length) return '';
    const starts = pairs.map(([start]) => String(start || '').toUpperCase()).filter(Boolean);
    // IMM-forward spreads/butterflies always use *Diff plot types (matches plot command parser).
    if (starts.length >= 2 && IMM_LABEL_RE.test(starts[0])) {
        if (pairs.length === 2) return 'fSpreadDiff';
        if (pairs.length === 3) return 'fBflyDiff';
        return '';
    }
    const allSameStart = starts.every((s) => s === starts[0]);
    if (pairs.length === 2) return allSameStart ? 'fSpread' : 'fSpreadDiff';
    if (pairs.length === 3) return allSameStart ? 'fBfly' : 'fBflyDiff';
    return '';
};

/** IMM query order matches plot/arc navigation: immFwd → imm → immLabel → plotType (plotType last). */
const getImmForwardPlotQueryFields = (fwdPairs, immYearByLabel) => {
    if (!fwdPairs.length || !IMM_LABEL_RE.test(fwdPairs[0][0])) return null;
    const starts = fwdPairs.map(([start]) => String(start || '').toUpperCase());
    const reversedStarts = starts.slice().reverse();
    const immFwd = reversedStarts[0] || '';
    const immLabel = reversedStarts[1] && IMM_LABEL_RE.test(reversedStarts[1]) ? reversedStarts[1] : '';
    const immLabel3 = reversedStarts[2] && IMM_LABEL_RE.test(reversedStarts[2]) ? reversedStarts[2] : '';
    let imm = '';
    let immYear = '';
    if (immLabel && typeof immYearByLabel?.[immLabel] === 'number') {
        immYear = String(immYearByLabel[immLabel]);
        imm = `${immYear}y`;
    }
    const plotType = getForwardPlotType(fwdPairs);
    return {
        immFwd, imm, immYear, immLabel, immLabel3, plotType
    };
};

const buildFwdLegsFromPairs = (fwdPairs, immYearByLabel) => {
    if (!fwdPairs.length) return '';
    return fwdPairs.flatMap(([start, end]) => {
        const s = String(start || '').trim();
        const e = String(end || '').trim();
        if (!s || !e) return [];
        if (IMM_LABEL_RE.test(s)) {
            const y = immYearByLabel?.[s.toUpperCase()];
            if (typeof y === 'number' && !Number.isNaN(y)) return [`${y}y`, e.toLowerCase()];
            return [`${s.toLowerCase()}${e.toLowerCase()}`];
        }
        return [s.toLowerCase(), e.toLowerCase()];
    }).join(',');
};

const joinQueryParts = (parts) => parts.filter(Boolean).join('&');

const appendImmFieldsToParts = (parts, immFields, { immWithYSuffix = true } = {}) => {
    if (!immFields) return;
    if (immFields.immFwd) parts.push(`immFwd=${immFields.immFwd}`);
    if (immFields.imm || immFields.immYear) {
        const immVal = immWithYSuffix
            ? (immFields.imm || `${immFields.immYear}y`)
            : (immFields.immYear || String(immFields.imm || '').replace(/y$/i, ''));
        if (immVal) parts.push(`imm=${immVal}`);
    }
    if (immFields.immLabel) parts.push(`immLabel=${immFields.immLabel}`);
    if (immFields.immLabel3) parts.push(`immLabel3=${immFields.immLabel3}`);
    if (immFields.plotType) parts.push(`plotType=${immFields.plotType}`);
};

/** Plot IRS query string aligned with arc/fwcm and global search (unencoded leg commas). */
const buildPlotIrsQueryString = ({
    legs = '',
    command = 'fwcm',
    immFwd = '',
    imm = '',
    immLabel = '',
    immLabel3 = '',
    plotType = '',
    advanced = false
} = {}) => {
    const parts = [];
    if (legs) parts.push(`legs=${legs}`);
    if (command) parts.push(`command=${command}`);
    appendImmFieldsToParts(parts, {
        immFwd, imm, immLabel, immLabel3, plotType
    });
    if (advanced) parts.push('advanced=true');
    return joinQueryParts(parts);
};

const buildArcLikeQueryString = ({ legs = '', immFields = null }) => {
    const parts = [];
    if (legs) parts.push(`legs=${legs}`);
    parts.push('plotCommand=fwcm');
    appendImmFieldsToParts(parts, immFields);
    return joinQueryParts(parts);
};

const buildChronosQueryString = ({ legs = '', immFields = null }) => {
    const parts = [];
    if (legs) parts.push(`legs=${legs}`);
    parts.push('command=fwcm');
    appendImmFieldsToParts(parts, immFields);
    return joinQueryParts(parts);
};

const buildAnchorQueryString = ({ curveId, legs = '', immFields = null }) => {
    const parts = [
        `l1=${legs}`,
        `depId=${curveId}`,
        `indepId=${curveId}`,
        'depF=1',
        'depImm=1'
    ];
    appendImmFieldsToParts(parts, immFields, { immWithYSuffix: false });
    return joinQueryParts(parts);
};

const buildBasisQueryString = ({
    curveId,
    legs = '',
    immFields = null
} = {}) => {
    const parts = [
        'command=BASIS',
        `depId=${curveId}`,
        `indepId=${DEFAULT_BASIS_REFERENCE_IRS_ID}`,
        `l1=${legs}`,
        `l2=${legs}`,
        'depF=1',
        'indepF=1',
        'depImm=1',
        'indepImm=1'
    ];
    appendImmFieldsToParts(parts, immFields);
    return joinQueryParts(parts);
};

const buildRegressImmQueryString = ({
    curveId,
    legs = '',
    immFields = null
} = {}) => {
    const parts = [
        `depId=${curveId}`,
        `indepId=${curveId}`,
        's2=1',
        'indepF=1'
    ];
    if (legs) parts.push(`l2=${legs}`);
    if (immFields) {
        parts.push('indepImm=1');
        appendImmFieldsToParts(parts, immFields, { immWithYSuffix: false });
    }
    return joinQueryParts(parts);
};

const appendForwardQueryParams = ({
    params,
    isFwdCurve,
    fwdPairs,
    fwdLegCount,
    immYearByLabel
}) => {
    if (!isFwdCurve) return;

    if (fwdPairs.length && IMM_LABEL_RE.test(fwdPairs[0][0])) {
        const immFields = getImmForwardPlotQueryFields(fwdPairs, immYearByLabel);
        if (!immFields) return;
        if (immFields.immFwd) params.set('immFwd', immFields.immFwd);
        if (immFields.imm) params.set('imm', immFields.imm);
        if (immFields.immLabel) params.set('immLabel', immFields.immLabel);
        if (immFields.immLabel3) params.set('immLabel3', immFields.immLabel3);
        if (immFields.plotType) params.set('plotType', immFields.plotType);
        return;
    }

    const forwardPlotType = getForwardPlotType(fwdPairs);
    if (forwardPlotType) params.set('plotType', forwardPlotType);
    if (fwdLegCount === 4) params.set('plotType', 'fSpread');
    if (fwdLegCount === 6) params.set('plotType', 'fBfly');
};

const appendRegressForwardParams = ({
    params,
    isFwdCurve,
    fwdPairs,
    fwdLegCount,
    immYearByLabel
}) => {
    if (!isFwdCurve) return;

    params.set('indepF', '1');

    const forwardPlotType = getForwardPlotType(fwdPairs);
    if (forwardPlotType) params.set('plotType', forwardPlotType);
    if (!forwardPlotType && fwdLegCount === 4) params.set('plotType', 'fSpread');
    if (!forwardPlotType && fwdLegCount === 6) params.set('plotType', 'fBfly');

    if (fwdPairs.length && IMM_LABEL_RE.test(fwdPairs[0][0])) {
        const immFields = getImmForwardPlotQueryFields(fwdPairs, immYearByLabel);
        if (!immFields) return;
        params.set('indepImm', '1');
        if (immFields.immFwd) params.set('immFwd', immFields.immFwd);
        if (immFields.imm) params.set('imm', immFields.imm);
        if (immFields.immLabel) params.set('immLabel', immFields.immLabel);
        if (immFields.immLabel3) params.set('immLabel3', immFields.immLabel3);
        if (immFields.plotType) params.set('plotType', immFields.plotType);
    }
};

const tenorRank = (tenor) => {
    const match = String(tenor || '').trim().toLowerCase().match(/^(\d+(?:\.\d+)?)([dwmy])$/);
    if (!match) return 0;
    const [, valueRaw, unit] = match;
    const value = Number(valueRaw);
    if (Number.isNaN(value)) return 0;
    const unitDays = { d: 1, w: 7, m: 30, y: 365 }[unit] || 0;
    return value * unitDays;
};

const getPlotfEndTenorLegs = (legsCsv = '') => {
    const legs = String(legsCsv || '')
        .split(',')
        .map((leg) => leg.trim().toLowerCase())
        .filter((leg) => TENOR_TOKEN_URL.test(leg));
    if (!legs.length) return '';

    const endLegs = legs.filter((_, idx) => idx % 2 === 1);
    if (!endLegs.length) return '';

    return endLegs.sort((a, b) => tenorRank(a) - tenorRank(b)).join(',');
};

/** Map stored structure to `legs` query: spreads use commas; explicit fwd pairs become `1y,2y` (matches plot `command=fwcm`). */
export const normalizeStructureForUrl = (structure) => {
    const raw = String(structure || '').trim();
    if (!raw) return '';
    const lower = raw
        .replace(/\s*[xX]\s*/g, ',')
        .replace(/\s+/g, ' ')
        .trim()
        .toLowerCase();

    const explicitFwd = lower.match(TENOR_PAIR_FWD);
    if (explicitFwd) {
        return `${explicitFwd[1].toLowerCase()},${explicitFwd[2].toLowerCase()}`;
    }

    if (/\bfwd\b/i.test(lower)) {
        const tenorTokens = lower.split(/[\s,]+/).filter((t) => TENOR_TOKEN_URL.test(t));
        if (tenorTokens.length >= 2 && tenorTokens.length % 2 === 0) {
            return tenorTokens.join(',').toLowerCase();
        }
        return lower.replace(/\s+/g, '');
    }

    const tokens = lower.split(/[\s,]+/).filter(Boolean);
    if (tokens.length >= 2 && tokens.every((t) => TENOR_TOKEN_URL.test(t))) {
        return tokens.join(',');
    }

    return lower.replace(/\s+/g, '');
};

export const getWorkspaceCommandHref = ({
    command,
    curveId,
    structure,
    isFwdCurve = false,
    immYearByLabel = null
}) => {
    const cmd = String(command || '').toUpperCase();
    if (!curveId) return null;

    const legs = normalizeStructureForUrl(structure);
    const fwdPairs = isFwdCurve ? parseForwardPairs(structure) : [];
    const immFields = fwdPairs.length && IMM_LABEL_RE.test(fwdPairs[0][0])
        ? getImmForwardPlotQueryFields(fwdPairs, immYearByLabel)
        : null;
    const fwdLegsFromPairs = buildFwdLegsFromPairs(fwdPairs, immYearByLabel);
    const defaultLegs = COMMANDS_REQUIRING_STRUCTURE.has(cmd) ? '1y' : '';
    const legsForCommandBase = fwdLegsFromPairs || legs || defaultLegs;
    const traceLegsCompact = (cmd === 'TRACE' && isFwdCurve && !immFields)
        ? legsForCommandBase.replace(/,/g, '')
        : legsForCommandBase;
    const legsForCommand = traceLegsCompact;
    const params = new URLSearchParams();
    const fwdLegCount = String(legsForCommandBase || '').split(',').map((s) => s.trim()).filter(Boolean).length;

    if (legsForCommand) params.set('legs', legsForCommand);

    switch (cmd) {
        case 'PLOT':
            if (isFwdCurve) params.set('command', 'fwcm');
            appendForwardQueryParams({
                params, isFwdCurve, fwdPairs, fwdLegCount, immYearByLabel
            });
            break;
        case 'PLOTA':
            params.set('advanced', 'true');
            if (isFwdCurve) params.set('command', 'fwcm');
            appendForwardQueryParams({
                params, isFwdCurve, fwdPairs, fwdLegCount, immYearByLabel
            });
            break;
        case 'BASIS':
            params.set('command', 'BASIS');
            params.set('depId', String(curveId));
            params.set('indepId', DEFAULT_BASIS_REFERENCE_IRS_ID);
            if (legsForCommandBase) {
                params.set('l1', legsForCommandBase);
                params.set('l2', legsForCommandBase);
            }
            if (isFwdCurve) {
                params.set('depF', '1');
                params.set('indepF', '1');
            }
            params.delete('legs');
            break;
        case 'PLOTF':
            if (isFwdCurve) {
                const plotfLegs = getPlotfEndTenorLegs(legsForCommandBase);
                if (legsForCommandBase) params.set('legs', legsForCommandBase);
                if (plotfLegs) params.set('parsedLegs', plotfLegs);
                params.set('plotCommand', 'fwcm');
                appendForwardQueryParams({
                    params, isFwdCurve, fwdPairs, fwdLegCount, immYearByLabel
                });
            }
            break;
        case 'STATS':
            if (isFwdCurve) params.set('plotCommand', 'fwcm');
            appendForwardQueryParams({
                params, isFwdCurve, fwdPairs, fwdLegCount, immYearByLabel
            });
            params.delete('command');
            break;
        case 'ARC':
        case 'EDGE':
            if (isFwdCurve) params.set('plotCommand', 'fwcm');
            appendForwardQueryParams({
                params, isFwdCurve, fwdPairs, fwdLegCount, immYearByLabel
            });
            break;
        case 'REGRESS':
            appendRegressForwardParams({
                params, isFwdCurve, fwdPairs, fwdLegCount, immYearByLabel
            });
            break;
        default:
            break;
    }

    const plotIrsQuery = (cmd === 'PLOT' || cmd === 'PLOTA') && immFields
        ? buildPlotIrsQueryString({
            legs: legsForCommand,
            command: 'fwcm',
            ...immFields,
            advanced: cmd === 'PLOTA'
        })
        : '';
    const arcLikeQuery = immFields && ['ARC', 'EDGE', 'STATS'].includes(cmd)
        ? buildArcLikeQueryString({ legs: legsForCommandBase, immFields })
        : '';
    const chronosQuery = immFields && cmd === 'TRACE'
        ? buildChronosQueryString({ legs: legsForCommandBase, immFields })
        : '';
    const anchorQuery = immFields && cmd === 'ANCHOR'
        ? buildAnchorQueryString({ curveId, legs: legsForCommandBase, immFields })
        : '';
    const ctaQuery = immFields && cmd === 'CTASCAN'
        ? buildArcLikeQueryString({ legs: legsForCommandBase, immFields })
        : '';
    const regressQuery = immFields && cmd === 'REGRESS'
        ? buildRegressImmQueryString({ curveId, legs: legsForCommandBase, immFields })
        : '';
    const basisQuery = immFields && cmd === 'BASIS'
        ? buildBasisQueryString({ curveId, legs: legsForCommandBase, immFields })
        : '';

    const qs = params.toString();
    const defaultQuery = qs ? `?${qs}` : '';

    switch (cmd) {
        case 'PLOT':
        case 'PLOTA':
            return `/plot/irs/${curveId}${plotIrsQuery ? `?${plotIrsQuery}` : defaultQuery}`;
        case 'BASIS':
            return `/plot/irs/${curveId}${basisQuery ? `?${basisQuery}` : defaultQuery}`;
        case 'TRACE':
            return `/chronos/${curveId}${chronosQuery ? `?${chronosQuery}` : defaultQuery}`;
        case 'ARC':
            return `/arc/irs/${curveId}${arcLikeQuery ? `?${arcLikeQuery}` : defaultQuery}`;
        case 'EDGE':
            return `/edge/irs/${curveId}${arcLikeQuery ? `?${arcLikeQuery}` : defaultQuery}`;
        case 'PLOTF':
            return `/plotf/irs/${curveId}${defaultQuery}`;
        case 'STATS':
            return `/stats/irs/${curveId}${arcLikeQuery ? `?${arcLikeQuery}` : defaultQuery}`;
        case 'CTASCAN':
            if (ctaQuery) return `/cta?id=${curveId}&${ctaQuery}`;
            return `/cta?id=${curveId}${legsForCommandBase ? `&legs=${legsForCommandBase}` : ''}`;
        case 'REGRESS':
            if (regressQuery) return `/regress?${regressQuery}`;
            return `/regress?depId=${curveId}&indepId=${curveId}${legsForCommandBase ? `&l2=${legsForCommandBase}` : ''}&s2=1${defaultQuery ? defaultQuery.replace('?', '&') : ''}`;
        case 'ANCHOR':
            if (anchorQuery) return `/anchor?${anchorQuery}`;
            return `/anchor?depId=${curveId}&indepId=${curveId}${legsForCommandBase ? `&l1=${legsForCommandBase}` : ''}${isFwdCurve ? '&depF=1' : ''}`;
        default:
            return null;
    }
};

/**
 * Structure + forward flag to send to workspace history after navigation.
 * Prefer URL-derived values so tracking matches the page we open (avoids stale tile labels).
 */
export const getWorkspaceTrackContextFromHref = (href, fallbacks = {}) => {
    const fallbackStructure = fallbacks.structure != null ? String(fallbacks.structure).trim() : '';
    const fallbackIsFwd = Boolean(fallbacks.isFwdCurve);
    if (!href || typeof href !== 'string') {
        return { structure: fallbackStructure || null, isFwdCurve: fallbackIsFwd };
    }
    try {
        const base = typeof window !== 'undefined' ? window.location.origin : 'http://localhost';
        const u = new URL(href, base);
        const q = u.searchParams;
        const rawLegParam = q.get('legs') || q.get('l1') || q.get('l2') || '';
        const structureFromUrl = rawLegParam
            ? rawLegParam.split(',')
                .map((s) => s.trim().toUpperCase())
                .filter(Boolean)
                .join(' ')
            : '';
        const structure = structureFromUrl || fallbackStructure || null;

        const cmdLower = String(q.get('command') || '').toLowerCase();
        const plotCommand = String(q.get('plotCommand') || '').toLowerCase();
        const isFwdCurve = Boolean(
            cmdLower === 'fwcm'
            || plotCommand === 'fwcm'
            || q.get('depF') === '1'
            || q.get('indepF') === '1'
            || q.get('depImm') === '1'
            || q.get('indepImm') === '1'
            || Boolean(q.get('immFwd'))
        );

        return { structure, isFwdCurve };
    } catch (_) {
        return { structure: fallbackStructure || null, isFwdCurve: fallbackIsFwd };
    }
};

const SKIP_WORKSPACE_HISTORY_ONCE_KEY = 'workspace:skipHistoryOnce';
const SKIP_WORKSPACE_HISTORY_TTL_MS = 90000;

/** After rail / search / tile navigation already recorded history, the destination page skips its own row. */
export const markSkipNextWorkspaceHistory = (curveId, command) => {
    if (typeof window === 'undefined') return;
    const cmd = String(command || '').toUpperCase();
    if (!cmd || cmd === 'HIST' || cmd === 'FAVS') return;
    try {
        sessionStorage.setItem(SKIP_WORKSPACE_HISTORY_ONCE_KEY, JSON.stringify({
            curveId: String(curveId || ''),
            command: cmd,
            at: Date.now()
        }));
    } catch (_) {
        // ignore
    }
};

/** @deprecated Use markSkipNextWorkspaceHistory */
export const markSkipNextPlotWorkspaceHistory = markSkipNextWorkspaceHistory;

/** One-shot: if marker matches curve + command, clear it and return true so the caller skips tracking. */
export const consumeSkipNextWorkspaceHistory = (curveId, command) => {
    if (typeof window === 'undefined') return false;
    const cmd = String(command || '').toUpperCase();
    if (!cmd) return false;
    try {
        const raw = sessionStorage.getItem(SKIP_WORKSPACE_HISTORY_ONCE_KEY);
        if (!raw) return false;
        const o = JSON.parse(raw);
        if (Date.now() - Number(o.at || 0) > SKIP_WORKSPACE_HISTORY_TTL_MS) {
            sessionStorage.removeItem(SKIP_WORKSPACE_HISTORY_ONCE_KEY);
            return false;
        }
        if (String(o.curveId) !== String(curveId || '') || String(o.command) !== cmd) {
            return false;
        }
        sessionStorage.removeItem(SKIP_WORKSPACE_HISTORY_ONCE_KEY);
        return true;
    } catch (_) {
        sessionStorage.removeItem(SKIP_WORKSPACE_HISTORY_ONCE_KEY);
        return false;
    }
};

/** @deprecated Use consumeSkipNextWorkspaceHistory */
export const consumeSkipNextPlotWorkspaceHistory = consumeSkipNextWorkspaceHistory;
